import React, { useEffect, useState, useCallback, useRef } from "react";
/* No react-bootstrap at all now. Modal/Row/Col/Card/Badge/ListGroup/Table
   all needed the Bootstrap stylesheet this project does not ship, so they
   rendered as unstyled divs; Form.Control was the last one left, and it
   held the mark fields, which is why those needed a wall of !important to
   style. The markup is the house kit -- pg-field, pg-label, pg-input. */
import {
  FaUser,
  /* FaPhone, not FaPhoneAlt, was the handset tilted the other way -- Font
     Awesome 5 flipped `phone` from the FA4 orientation and kept the old one
     as `phone-alt`. It read as a mirrored icon next to every other glyph
     here. This is the only phone icon in the app, so there is nothing to
     keep it consistent with. */
  FaPhoneAlt,
  FaTimes,
  FaInfoCircle,
  FaGraduationCap,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaBook,
  FaUsers,
  FaUserFriends,
  FaComments,
  FaLaptopCode,
  FaEdit,
  FaUndo,
  FaAward,
  FaMinus,
  FaPlus,
} from "react-icons/fa";
import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone.jsx';
import {
  submitStudentMockInterviewResult,
  getStudentAttendance,
  getStudentQuizzes,
  getStudentMockInterviewResult,
} from "../../../services/MockInterview";
import NewCustomDataTable from "../common/NewCustomDataTable";
import axiosInstance from "../../../services/AxiosInstance";
/* The dialog is drawn with the page kit (pg-) and the batch popup's own
   body kit (mk- and mkm-). Both are imported by the parent already, but
   naming them here keeps this file readable on its own. */
import "../Trainer/TrainerDashboard.css";
import "./MockInterviewPage.css";
import "./MockInterview.css";

// ─── Fetch students for a batch + trainer ─────────────────────────────────────
// GET /trainerdashboard/mystudents?page=1&limit=20&search=&batchId=...&trainerId=...
const fetchBatchStudents = async (batchId, trainerId, page = 1, limit = 10, search = "") => {
  const resp = await axiosInstance.get(
    `/trainerdashboard/mystudents?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&batchId=${batchId}&trainerId=${trainerId}`
  );
  return {
    data: resp.data?.data || [],
    pagination: resp.data?.pagination || {},
  };
};

// ─── Component ────────────────────────────────────────────────────────────────
const StudentDetailModal = ({
  show,
  onHide,
  studentData,
  onEvaluationSaved,
  /* The roster button that opens this already says "Evaluate" or "Edit
     marks", so arriving at a read-only panel with an "Edit evaluation"
     button on it asks the same question twice. With this the dialog opens
     in the state the caller asked for; Cancel edit still drops it back to
     read-only, which is where the Edit button belongs. */
  editOnOpen = false,
}) => {

  const isListMode = !studentData?.student;

  // ── Students table state (list mode) ────────────────────────────────────────
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(5);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [appliedStudentSearch, setAppliedStudentSearch] = useState("");

  // Debounce timer ref
  const searchDebounceTimer = useRef(null);

  // ── Selected student for detail view (list mode → detail) ───────────────────
  const [detailStudent, setDetailStudent] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // ── Attendance (detail mode) ─────────────────────────────────────────────────
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // ── Quiz history (detail mode) ───────────────────────────────────────────────
  const [quizHistory, setQuizHistory] = useState([]);
  const [loadingQuiz, setLoadingQuiz] = useState(false);

  // ── Mock interview form ──────────────────────────────────────────────────────
  const [mockInterviewData, setMockInterviewData] = useState({
    description: "",
    communicationMark: "",
    technicalMark: "",
    marks: "",
    totalMarks: "20",
    pass: false,
    fail: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingMockResult, setHasExistingMockResult] = useState(false);
  const [isEditingEvaluation, setIsEditingEvaluation] = useState(false);
  const [loadingMockResult, setLoadingMockResult] = useState(false);

  const isFetchingStudents = useRef(false);

  // ── Derived values ───────────────────────────────────────────────────────────
  const batchId = studentData?.batchId;
  const actualTrainerId = studentData?.actualTrainerId;
  const eventdata = studentData?.eventdata;

  // Get batch name from eventdata (passed from parent)
  const batchName = eventdata?.batchId?.[0]?.name || "N/A";

  // ── Reset when modal closes ──────────────────────────────────────────────────
  useEffect(() => {
    if (!show) {
      setStudents([]);
      setTotalStudents(0);
      setStudentsPage(1);
      setStudentsLoading(false);
      setStudentSearchTerm("");
      setAppliedStudentSearch("");
      setDetailStudent(null);
      setShowDetail(false);
      setPresentCount(0);
      setAbsentCount(0);
      setBatchCount(0);
      setAttendanceRecords([]);
      setQuizHistory([]);
      setMockInterviewData({
        description: "",
        communicationMark: "",
        technicalMark: "",
        marks: "",
        totalMarks: "20",
        pass: false,
        fail: false,
      });
      setHasExistingMockResult(false);
      setIsEditingEvaluation(false);
      
      // Clear any pending debounce timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    }
  }, [show]);

  // ─── Handle search with debounce ─────────────────────────────────────────────
  const handleSearchChange = useCallback((val) => {
    setStudentSearchTerm(val);
    
    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Set new timer for 700ms debounce
    searchDebounceTimer.current = setTimeout(() => {
      setAppliedStudentSearch(val);
      setStudentsPage(1); // Reset to first page on new search
    }, 700);
  }, []);

  // ─── Fetch students list ──────────────────────────────────────────────────────
  const loadStudents = useCallback(async () => {
    if (!batchId || !actualTrainerId || isFetchingStudents.current) return;
    isFetchingStudents.current = true;
    setStudentsLoading(true);
    try {
      const resp = await fetchBatchStudents(
        batchId,
        actualTrainerId,
        studentsPage,
        studentsPerPage,
        appliedStudentSearch
      );
      setStudents(resp.data);
      setTotalStudents(resp.pagination?.total || resp.data.length);
    } catch (err) {
      console.error("Error fetching batch students:", err);
      toast.error("Failed to load students for this batch");
    } finally {
      setStudentsLoading(false);
      isFetchingStudents.current = false;
    }
  }, [batchId, actualTrainerId, studentsPage, studentsPerPage, appliedStudentSearch]);

  // Load students when page, per page, or search changes
  useEffect(() => {
    if (show && isListMode && batchId && actualTrainerId) {
      loadStudents();
    }
  }, [show, isListMode, loadStudents]);

  // ─── Check existing mock interview result for a student ───────────────────────
  const checkExistingMockResult = useCallback(async (student, startEditing = false) => {
    if (!student || !batchId || !actualTrainerId || !eventdata?.id) {
      return;
    }

    if (student.evalData && student.evalData.id) {
      const ev = student.evalData;
      setHasExistingMockResult(true);
      setIsEditingEvaluation(startEditing);
      const comm = ev.communicationMark != null ? ev.communicationMark.toString() : "";
      const tech = ev.technicalMark != null ? ev.technicalMark.toString() : "";
      const total = ev.totalMark != null ? ev.totalMark.toString() : "20";
      const mark = ev.mark != null ? ev.mark.toString() : (comm !== "" && tech !== "" ? (Number(comm) + Number(tech)).toString() : "");
      setMockInterviewData({
        description: ev.description || ev.evalDescription || "",
        communicationMark: comm,
        technicalMark: tech,
        marks: mark,
        totalMarks: total,
        pass: ev.result === "Qualified" || ev.result === "pass",
        fail: ev.result === "Unqualified" || ev.result === "fail",
      });
    }
    
    try {
      setLoadingMockResult(true);
      const studentIdValue = student.studentId || student.id || student.userId || student._id;
      
      const response = await getStudentMockInterviewResult(
        actualTrainerId,
        batchId,
        studentIdValue,
        eventdata.id
      );
      
      if (response.data && response.data.id) {
        // Existing result found
        setHasExistingMockResult(true);
        setIsEditingEvaluation(startEditing);
        
        const comm = response.data.communicationMark != null ? response.data.communicationMark.toString() : "";
        const tech = response.data.technicalMark != null ? response.data.technicalMark.toString() : "";
        const total = response.data.totalMark != null ? response.data.totalMark.toString() : "20";
        const mark = response.data.mark != null
          ? response.data.mark.toString()
          : (comm !== "" && tech !== "" ? (Number(comm) + Number(tech)).toString() : "");

        setMockInterviewData({
          description: response.data.description || "",
          communicationMark: comm,
          technicalMark: tech,
          marks: mark,
          totalMarks: total,
          pass: response.data.result === "Qualified" || response.data.result === "pass",
          fail: response.data.result === "Unqualified" || response.data.result === "fail",
        });
      } else if (!student.evalData?.id) {
        // No existing result
        setHasExistingMockResult(false);
        setIsEditingEvaluation(false);
        setMockInterviewData({
          description: "",
          communicationMark: "",
          technicalMark: "",
          marks: "",
          totalMarks: "20",
          pass: false,
          fail: false,
        });
      }
    } catch (err) {
      console.error("Error checking existing mock interview result:", err);
      if (!student.evalData?.id) {
        setHasExistingMockResult(false);
        setIsEditingEvaluation(false);
        setMockInterviewData({
          description: "",
          communicationMark: "",
          technicalMark: "",
          marks: "",
          totalMarks: "20",
          pass: false,
          fail: false,
        });
      }
    } finally {
      setLoadingMockResult(false);
    }
  }, [batchId, actualTrainerId, eventdata]);

  // ─── Attendance for a student using new endpoint ─────────────────────────────────
  const getAttendanceData = useCallback(async (student) => {
    if (!student || !batchId || !actualTrainerId) return;
    
    try {
      setLoadingAttendance(true);
      const studentIdValue = student.studentId || student.id || student.userId;
      
      const response = await getStudentAttendance(batchId, studentIdValue, actualTrainerId);
      const allAttendances = response.data || [];

      let present = 0, absent = 0;
      const records = [];

      allAttendances.forEach((record) => {
        // Count attendance based on status field from response
        if (record.status === "Present") {
          present++;
        } else if (record.status === "Absent") {
          absent++;
        }

        records.push({
          date: record.date || record.createdAt,
          status: record.status || (record.userIds?.includes(studentIdValue) ? "Present" : "Absent"),
          subject: record.subject || record.syllabusId || "N/A",
        });
      });

      setPresentCount(present);
      setAbsentCount(absent);
      setBatchCount(allAttendances.length);
      setAttendanceRecords(records);
    } catch (err) {
      console.error("Error fetching attendance:", err);
      toast.error("Failed to load attendance data");
      setPresentCount(0);
      setAbsentCount(0);
      setBatchCount(0);
      setAttendanceRecords([]);
    } finally {
      setLoadingAttendance(false);
    }
  }, [batchId, actualTrainerId]);

  // ── Quiz history for a student using new endpoint ───────────────────────────────
  const getQuizHistory = useCallback(async (student) => {
    if (!student || !batchId || !actualTrainerId) return;
    
    try {
      setLoadingQuiz(true);
      const studentIdValue = student.studentId || student.id || student.userId;
      
      const response = await getStudentQuizzes(studentIdValue, batchId, actualTrainerId);
      const quizzes = response.data || [];

      // Transform the data to match the expected format
      const formattedQuizzes = quizzes.map((quiz) => ({
        quizId: quiz.quizId,
        quizName: quiz.quizName || "N/A",
        result: quiz.result,
        score: quiz.scored,
        outOf: quiz.outOf,
        timeConsumed: quiz.timeConsumed,
        startDate: quiz.startDate,
        endDate: quiz.endDate,
        createdAt: quiz.createdAt,
      }));

      setQuizHistory(formattedQuizzes);
    } catch (err) {
      console.error("Error fetching quiz history:", err);
      toast.error("Failed to load quiz history");
      setQuizHistory([]);
    } finally {
      setLoadingQuiz(false);
    }
  }, [batchId, actualTrainerId]);

  // ── Open individual student detail (from table row) ──────────────────────────
  const openStudentDetail = useCallback(async (student) => {
    setDetailStudent(student);
    setShowDetail(true);
    // Reset form and existing result state
    setMockInterviewData({ description: "", marks: "", totalMarks: "", pass: false, fail: false });
    setHasExistingMockResult(false);
    
    // Load all student data in parallel
    await Promise.all([
      getAttendanceData(student),
      getQuizHistory(student),
      checkExistingMockResult(student, editOnOpen)
    ]);
  }, [getAttendanceData, getQuizHistory, checkExistingMockResult, editOnOpen]);

  // If in direct student detail mode (not list mode), auto-load attendance/quiz
  useEffect(() => {
    if (show && !isListMode && studentData?.student) {
      const loadStudentData = async () => {
        await Promise.all([
          getAttendanceData(studentData.student),
          getQuizHistory(studentData.student),
          checkExistingMockResult(studentData.student, editOnOpen)
        ]);
      };
      loadStudentData();
    }
  }, [show, isListMode, studentData, getAttendanceData, getQuizHistory, checkExistingMockResult, editOnOpen]);

  // ── Mock interview form handlers ─────────────────────────────────────────────
  const handleMockInterviewChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      if (name === "pass") {
        setMockInterviewData((prev) => ({ ...prev, pass: checked, fail: checked ? false : prev.fail }));
      } else if (name === "fail") {
        setMockInterviewData((prev) => ({ ...prev, fail: checked, pass: checked ? false : prev.pass }));
      }
    } else if (name === "communicationMark" || name === "technicalMark") {
      let val = value;
      // Clamp between 0 and 10
      if (val !== "" && !isNaN(Number(val))) {
        const num = Number(val);
        if (num > 10) val = "10";
        if (num < 0) val = "0";
      }
      setMockInterviewData((prev) => {
        const next = { ...prev, [name]: val };
        const comm = next.communicationMark !== "" ? Number(next.communicationMark) : NaN;
        const tech = next.technicalMark !== "" ? Number(next.technicalMark) : NaN;
        
        if (!isNaN(comm) && !isNaN(tech)) {
          const sum = comm + tech;
          next.marks = sum.toString();
          // Auto-suggest qualified if total >= 10 (50%) when user hasn't explicitly picked yet
          if (!prev.pass && !prev.fail) {
            if (sum >= 10) {
              next.pass = true;
              next.fail = false;
            } else {
              next.pass = false;
              next.fail = true;
            }
          }
        } else if (!isNaN(comm)) {
          next.marks = comm.toString();
        } else if (!isNaN(tech)) {
          next.marks = tech.toString();
        } else {
          next.marks = "";
        }
        return next;
      });
    } else {
      setMockInterviewData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* The steppers. A number field you can only type into is a number
     field people mistype: these marks move in halves between 0 and 10,
     which is four keystrokes of precision nobody wants to supply. The
     change is pushed back through handleMockInterviewChange rather than
     setting state here, so the total, the clamp and the auto-suggested
     result all stay in one place. */
  const stepMark = (name, by) => {
    const current = Number(mockInterviewData[name]);
    const from = Number.isNaN(current) ? 0 : current;
    const next = Math.min(10, Math.max(0, Math.round((from + by) * 2) / 2));
    handleMockInterviewChange({
      target: { name, value: String(next), type: "number" },
    });
  };

  const handleMockInterviewSubmit = async (student) => {
    try {
      setSubmitting(true);
      const { description, communicationMark, technicalMark, marks, totalMarks, pass, fail } = mockInterviewData;

      if (communicationMark === "" || communicationMark === null || communicationMark === undefined) {
        toast.error("Please enter Communication Skill marks (0-10)");
        return;
      }
      if (technicalMark === "" || technicalMark === null || technicalMark === undefined) {
        toast.error("Please enter Technical Skill marks (0-10)");
        return;
      }

      const commNum = Number(communicationMark);
      const techNum = Number(technicalMark);

      if (isNaN(commNum) || commNum < 0 || commNum > 10) {
        toast.error("Communication Skill marks must be between 0 and 10");
        return;
      }
      if (isNaN(techNum) || techNum < 0 || techNum > 10) {
        toast.error("Technical Skill marks must be between 0 and 10");
        return;
      }

      if (!pass && !fail) {
        toast.error("Please select Result (Qualified or Unqualified)");
        return;
      }

      const totalObtained = commNum + techNum;
      const totalPossible = Number(totalMarks) || 20;

      const submitData = {
        eveventId: eventdata?.id || eventdata?._id || "",
        trainerId: actualTrainerId || "",
        studentId: student?.studentId || student?.id || student?.userId || student?._id || "",
        batchId: batchId || "",
        description: description || "Evaluation completed",
        communicationMark: commNum,
        technicalMark: techNum,
        mark: totalObtained,
        totalMark: totalPossible,
        result: pass ? "Qualified" : "Unqualified",
        createdBy: "NA",
      };

      await submitStudentMockInterviewResult(submitData);
      toast.success(
        hasExistingMockResult
          ? "Mock interview evaluation updated successfully!"
          : "Mock interview evaluation submitted successfully!"
      );
      setHasExistingMockResult(true);
      setIsEditingEvaluation(false);
      await checkExistingMockResult(student);
      if (onEvaluationSaved) {
        onEvaluationSaved();
      }
    } catch (err) {
      console.error("Error submitting mock interview:", err);
      toast.error(err?.response?.data?.message || "Failed to submit mock interview data");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Students table columns ────────────────────────────────────────────────────
  const studentHeaders = [
    {
      title: "Sr. No",
      prop: "srno",
    },
    {
      title: "Name",
      prop: "name",
      sortable: true,
      filterable: true,
      cell: (row) => (
        <button
          type="button"
          className="sdm-namelink"
          onClick={() => openStudentDetail(row)}
        >
          {row.name || row.studentName || "N/A"}
        </button>
      ),
    },
    {
      title: "Email",
      prop: "email",
      sortable: true,
      filterable: true,
      cell: (row) => <span>{row.email || row.studentEmail || "N/A"}</span>,
    },
    {
      title: "Phone",
      prop: "phone",
      cell: (row) => <span>{row.phone || row.studentNumber || row.mobileNumber || "N/A"}</span>,
    },
    {
      title: "Action",
      prop: "action",
      cell: (row) => (
        <button
          type="button"
          className="mk-btn"
          onClick={() => openStudentDetail(row)}
        >
          <FaUser aria-hidden="true" /> View
        </button>
      ),
    },
  ];

  // ── Attendance % ──────────────────────────────────────────────────────────────
  const attendancePercentage =
    batchCount > 0 ? ((presentCount / batchCount) * 100).toFixed(2) : 0;

  // ── Emergency contacts ───────────────────────────────────────────────────────
  const renderEmergencyContacts = (student) => {
    const contacts = student?.contactPersonIds || [];

    if (!contacts.length) {
      return <p className="sdm-empty">No emergency contact on record.</p>;
    }

    return (
      <div className="sdm-contacts">
        {contacts.map((contact, index) => (
          <div className="sdm-contact" key={contact.id || index}>
            <span className="sdm-contact-mark">
              <FaUserFriends aria-hidden="true" />
            </span>
            <div className="sdm-contact-text">
              <span className="sdm-contact-name">{contact.name || "Unnamed contact"}</span>
              <span className="sdm-contact-phone">
                <FaPhoneAlt aria-hidden="true" />
                {contact.phone || "N/A"}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Student detail panel (rendered inside the modal body) ────────────────────
  /* This was <Row>/<Col>/<Card>/<ListGroup>/<Badge>/<Table> plus some fifty
     Bootstrap utility classes -- d-flex, gap-2, me-2, mb-4, fw-bold, fs-6,
     text-muted, text-center, bg-dark, border-0, shadow-sm. This project
     ships no Bootstrap stylesheet, so none of them resolved: the cards had
     no panel or padding, the two md={6} columns never sat side by side and
     the badges were bare text. What styling this dialog once had came from
     the `.premium-modal ...` rules in popups.css, and the shell stopped
     being a .premium-modal when it moved to the house .pg-modal -- which is
     what left the body looking undesigned next to the batch popup that
     opens it.

     Everything below is the kit that batch popup already uses -- mkm-summary,
     mkm-section, mkm-facts, mkm-table, mkm-pill, pg-state -- so the two read
     as one flow.

     The evaluation also moved to the top: this dialog is opened by
     "Evaluate" / "Edit marks", and its form used to sit below two full
     tables. */
  const renderStudentDetail = (student) => {
    if (!student) return null;

    const studentName = student.name || student.studentName || "Unnamed student";
    const studentEmail = student.email || student.studentEmail || "";
    const studentPhone = student.phone || student.studentNumber || student.mobileNumber || "";
    const locked = hasExistingMockResult && !isEditingEvaluation;
    const totalPossible = Number(mockInterviewData.totalMarks) || 20;
    const scored = mockInterviewData.marks !== "" ? Number(mockInterviewData.marks) : null;
    const identity = [
      studentEmail,
      studentPhone,
      batchName !== "N/A" ? `Batch: ${batchName}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <>
        {/* ── Who this is ──────────────────────────────────────────────── */}
        <div className="mkm-summary">
          <span className="mkm-summary-mark">
            <FaUser aria-hidden="true" />
          </span>
          <div className="mkm-summary-text">
            <h5 className="mkm-summary-name">{studentName}</h5>
            <p className="mkm-summary-sub">
              {identity || "No contact details on record"}
            </p>
          </div>
          {hasExistingMockResult ? (
            <span
              className={`mkm-pill ${mockInterviewData.pass ? "mkm-pill--ok" : "mkm-pill--bad"}`}
            >
              {mockInterviewData.pass ? (
                <FaCheckCircle aria-hidden="true" />
              ) : (
                <FaTimesCircle aria-hidden="true" />
              )}
              {mockInterviewData.pass ? "Qualified" : "Unqualified"}
            </span>
          ) : (
            <span className="mkm-pill mkm-pill--wait">Not evaluated</span>
          )}
        </div>

        {/* ── Evaluation: the reason the dialog is open ────────────────── */}
        <section className="mkm-section">
          <div className="mkm-section-head">
            <h4 className="mkm-section-title">
              <FaAward aria-hidden="true" />
              Mock interview evaluation
            </h4>

            {hasExistingMockResult && !isEditingEvaluation && (
              <button
                type="button"
                className="mk-btn"
                onClick={() => setIsEditingEvaluation(true)}
              >
                <FaEdit aria-hidden="true" /> Edit evaluation
              </button>
            )}
            {isEditingEvaluation && (
              <button
                type="button"
                className="mk-btn"
                onClick={() => {
                  setIsEditingEvaluation(false);
                  checkExistingMockResult(student);
                }}
              >
                <FaUndo aria-hidden="true" /> Cancel edit
              </button>
            )}
          </div>

          {loadingMockResult ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading this student&apos;s evaluation...</p>
            </div>
          ) : (
            <div className="mkm-eval">
              {/* The two marks. Label above figure, one hairline, no tinted
                  fills -- the house shape. Each is a stepper rather than a
                  bare number field: the scale is 0-10 in halves, the native
                  spinner is four pixels tall and unusable, and typing is
                  still there for anyone who prefers it. */}
              <div className="mkm-score-grid">
                {[
                  {
                    name: "communicationMark",
                    label: "Communication",
                    Icon: FaComments,
                    hint: "Clarity, confidence, fluency and professional presentation",
                  },
                  {
                    name: "technicalMark",
                    label: "Technical",
                    Icon: FaLaptopCode,
                    hint: "Core domain concepts, coding and problem-solving ability",
                  },
                ].map(({ name, label, Icon, hint }) => {
                  const raw = mockInterviewData[name];
                  const value = raw === "" ? null : Number(raw);
                  return (
                    <div className="mkm-score" key={name}>
                      <div className="mkm-score-head">
                        <label className="mkm-score-label" htmlFor={`mkm-${name}`}>
                          <Icon aria-hidden="true" />
                          {label}
                        </label>
                        <span className="mkm-score-of">out of 10</span>
                      </div>

                      <div className="mkm-stepper">
                        <button
                          type="button"
                          className="mkm-step"
                          aria-label={`Lower ${label.toLowerCase()} mark`}
                          disabled={locked || (value !== null && value <= 0)}
                          onClick={() => stepMark(name, -0.5)}
                        >
                          <FaMinus aria-hidden="true" />
                        </button>

                        <span className="mkm-score-box">
                          <input
                            id={`mkm-${name}`}
                            className="mkm-score-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            max="10"
                            step="0.5"
                            placeholder="0"
                            name={name}
                            value={raw}
                            onChange={handleMockInterviewChange}
                            disabled={locked}
                          />
                          {/* aria-hidden: the field already says "out of 10"
                              in its own label, and a screen reader reading
                              "slash ten" after every keystroke is noise. */}
                          <span className="mkm-score-unit" aria-hidden="true">
                            / 10
                          </span>
                        </span>

                        <button
                          type="button"
                          className="mkm-step"
                          aria-label={`Raise ${label.toLowerCase()} mark`}
                          disabled={locked || (value !== null && value >= 10)}
                          onClick={() => stepMark(name, 0.5)}
                        >
                          <FaPlus aria-hidden="true" />
                        </button>
                      </div>

                      <p className="mkm-score-hint">{hint}</p>
                    </div>
                  );
                })}
              </div>

              {/* What the two marks add up to, and the call made on them.
                  Label above figure, and the percentage is the one coloured
                  thing here -- colour as a tag, not as the headline. */}
              <div className="mkm-eval-bar">
                <div className="mkm-total">
                  <span className="mkm-total-label">Total score</span>
                  <span className="mkm-total-value">
                    {scored != null ? scored : 0}
                    <span className="mkm-total-of"> / {totalPossible}</span>
                  </span>
                </div>

                {scored != null && (
                  <span
                    className={`mkm-pill ${
                      scored >= totalPossible / 2 ? "mkm-pill--ok" : "mkm-pill--wait"
                    }`}
                  >
                    {((scored / totalPossible) * 100).toFixed(0)}%
                  </span>
                )}

                <div className="mkm-result" role="group" aria-label="Result">
                  {[
                    { key: "pass", label: "Qualified", Icon: FaCheckCircle, tone: "ok" },
                    { key: "fail", label: "Unqualified", Icon: FaTimesCircle, tone: "bad" },
                  ].map(({ key, label, Icon, tone }) => (
                    <button
                      key={key}
                      type="button"
                      className={`mkm-result-btn mkm-result-btn--${tone}${
                        mockInterviewData[key] ? " is-on" : ""
                      }`}
                      aria-pressed={Boolean(mockInterviewData[key])}
                      disabled={locked}
                      onClick={() =>
                        setMockInterviewData((prev) => ({
                          ...prev,
                          pass: key === "pass",
                          fail: key === "fail",
                        }))
                      }
                    >
                      <Icon aria-hidden="true" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pg-field">
                <label className="pg-label" htmlFor="sdm-feedback">
                  Feedback &amp; observations
                </label>
                <textarea
                  id="sdm-feedback"
                  className="pg-input pg-textarea"
                  rows={3}
                  placeholder="Strengths, gaps and what to work on before the next round..."
                  name="description"
                  value={mockInterviewData.description}
                  onChange={handleMockInterviewChange}
                  disabled={locked}
                />
              </div>
            </div>
          )}
        </section>

        {/* ── Profile ──────────────────────────────────────────────────── */}
        <section className="mkm-section">
          <h4 className="mkm-section-title">
            <FaInfoCircle aria-hidden="true" />
            Personal information
          </h4>
          <dl className="mkm-facts">
            <div className="mkm-fact">
              <dt>Name</dt>
              <dd>{studentName}</dd>
            </div>
            <div className="mkm-fact">
              <dt>Batch</dt>
              <dd>{batchName}</dd>
            </div>
            <div className="mkm-fact">
              <dt>Email</dt>
              <dd>{studentEmail || "N/A"}</dd>
            </div>
            <div className="mkm-fact">
              <dt>Phone</dt>
              <dd>{studentPhone || "N/A"}</dd>
            </div>
            <div className="mkm-fact">
              <dt>City</dt>
              <dd>{student.city || "N/A"}</dd>
            </div>
            <div className="mkm-fact">
              <dt>Profession</dt>
              <dd>{student.profession || "N/A"}</dd>
            </div>
          </dl>
        </section>

        {/* ── Emergency contacts ───────────────────────────────────────── */}
        <section className="mkm-section">
          <h4 className="mkm-section-title">
            <FaUserFriends aria-hidden="true" />
            Emergency contact(s)
          </h4>
          {renderEmergencyContacts(student)}
        </section>

        {/* ── Attendance ───────────────────────────────────────────────── */}
        <section className="mkm-section">
          <h4 className="mkm-section-title">
            <FaCalendarAlt aria-hidden="true" />
            Attendance
          </h4>

          {loadingAttendance ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading attendance records...</p>
            </div>
          ) : (
            <>
              <div className="sdm-tiles">
                {[
                  { label: "Total lectures", value: batchCount },
                  { label: "Present", value: presentCount },
                  { label: "Absent", value: absentCount },
                  { label: "Attendance", value: `${attendancePercentage}%` },
                ].map((item) => (
                  <div className="sdm-tile" key={item.label}>
                    <span className="sdm-tile-value">{item.value}</span>
                    <span className="sdm-tile-label">{item.label}</span>
                  </div>
                ))}
              </div>

              {attendanceRecords.length > 0 ? (
                <div className="mkm-table-wrap sdm-table-wrap">
                  <table className="mkm-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Subject</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceRecords.map((record, index) => (
                        <tr key={index}>
                          <td>
                            {record.date
                              ? new Date(record.date).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td>
                            <span className="mkm-mark">
                              <FaBook aria-hidden="true" /> {record.subject}
                            </span>
                          </td>
                          <td>
                            {record.status === "Present" ? (
                              <span className="mkm-pill mkm-pill--ok">
                                <FaCheckCircle aria-hidden="true" /> Present
                              </span>
                            ) : (
                              <span className="mkm-pill mkm-pill--bad">
                                <FaTimesCircle aria-hidden="true" /> Absent
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="sdm-empty">No attendance records for this student yet.</p>
              )}
            </>
          )}
        </section>

        {/* ── Quiz history ─────────────────────────────────────────────── */}
        <section className="mkm-section">
          <h4 className="mkm-section-title">
            <FaGraduationCap aria-hidden="true" />
            Quiz history
            {batchName !== "N/A" && <span className="mkm-section-note">{batchName}</span>}
          </h4>

          {loadingQuiz ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading quiz history...</p>
            </div>
          ) : quizHistory.length > 0 ? (
            <div className="mkm-table-wrap sdm-table-wrap">
              <table className="mkm-table">
                <thead>
                  <tr>
                    <th>Quiz</th>
                    <th>Result</th>
                    <th>Score</th>
                    <th>Time</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {quizHistory.map((quiz, index) => (
                    <tr key={index}>
                      <td>
                        <span className="mkm-student">{quiz.quizName}</span>
                      </td>
                      <td>
                        {quiz.result === "Pass" ? (
                          <span className="mkm-pill mkm-pill--ok">
                            <FaCheckCircle aria-hidden="true" /> Pass
                          </span>
                        ) : (
                          <span className="mkm-pill mkm-pill--bad">
                            <FaTimesCircle aria-hidden="true" /> Fail
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="mkm-total">
                          {quiz.score}
                          <span className="mkm-total-of"> / {quiz.outOf}</span>
                        </span>
                      </td>
                      <td>{quiz.timeConsumed || <span className="mkm-na">N/A</span>}</td>
                      <td>
                        {quiz.startDate ? (
                          new Date(quiz.startDate).toLocaleDateString()
                        ) : (
                          <span className="mkm-na">N/A</span>
                        )}
                      </td>
                      <td>
                        {quiz.endDate ? (
                          new Date(quiz.endDate).toLocaleDateString()
                        ) : (
                          <span className="mkm-na">N/A</span>
                        )}
                      </td>
                      <td>
                        {quiz.createdAt ? (
                          new Date(quiz.createdAt).toLocaleDateString()
                        ) : (
                          <span className="mkm-na">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="sdm-empty">No quiz history for this student in this batch.</p>
          )}
        </section>
      </>
    );
  };

  // ── Students list (indexed for table) ────────────────────────────────────────
  const indexedStudents = students.map((s, i) => ({
    ...s,
    srno: (studentsPage - 1) * studentsPerPage + i + 1,
  }));

  // A saved evaluation is confirmed in this dialog, which then closes itself.
  const { done } = usePopupDone(Boolean(show && studentData));

  // ── Guard: nothing to show ────────────────────────────────────────────────────
  if (!studentData) return null;

  // ── Decide what to render in modal body ──────────────────────────────────────
  const renderBody = () => {
    // Direct student detail mode (studentData.student is set)
    if (!isListMode) {
      return renderStudentDetail(studentData.student);
    }

    // List mode → showing individual student detail
    if (isListMode && showDetail && detailStudent) {
      return renderStudentDetail(detailStudent);
    }

    // List mode → students table
    return (
      <>
        <div className="mkm-section-head">
          <h4 className="mkm-section-title">
            <FaUsers aria-hidden="true" />
            Students in batch
            {batchName !== "N/A" && <span className="mkm-section-note">{batchName}</span>}
          </h4>
          <span className="mkm-pill">{totalStudents} students</span>
        </div>

        <NewCustomDataTable
          data={indexedStudents}
          headers={studentHeaders}
          totalRecords={totalStudents}
          currentPage={studentsPage}
          recordsPerPage={studentsPerPage}
          onPageChange={(page) => setStudentsPage(page)}
          onRowsPerPageChange={(limit) => { 
            setStudentsPerPage(limit); 
            setStudentsPage(1); 
          }}
          searchTerm={studentSearchTerm}
          onSearchChange={handleSearchChange}
          searchableFields={[
            { value: "name", label: "Name" },
            { value: "email", label: "Email" },
          ]}
          serverSide={true}
          loading={studentsLoading}
          pageTitle="Students"
          exportButton={false}
          addButton={false}
        />
      </>
    );
  };

  // ── Modal head ────────────────────────────────────────────────────────────────
  /* The title used to read "Student Details" for every student in the
     batch, which is the one thing the person evaluating already knows.
     It is the student's name now, with the session underneath. */
  const headStudent = isListMode ? (showDetail ? detailStudent : null) : studentData?.student;

  const modalTitle = () => {
    if (headStudent) {
      return headStudent.name || headStudent.studentName || "Student details";
    }
    return `Students in batch: ${batchName}`;
  };

  const modalSub = () => {
    if (!headStudent) return "All enrolled students · click a name to open a profile";
    return [
      eventdata?.name,
      batchName !== "N/A" ? `Batch: ${batchName}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "Profile, attendance, quizzes and evaluation";
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (!show) return null;

  return (
    /* The house pg-modal, the same shell List Attendance's view popup
       and the trainer dashboard's batch popup use.

       It was a react-bootstrap <Modal className="premium-modal">, and
       react-bootstrap ships no CSS -- the dialog had no panel, no
       radius, no centring and no max-height, so it filled the viewport
       edge to edge with its content running off the bottom.

       This copy is opened from one place, MockInterView.jsx's batch
       popup ("Evaluate" / "Edit marks"); the trainer and admin
       dashboards use Trainer/StudentDetailModal.jsx, a separate
       component. */
    <div className="pg-modal" role="presentation" onClick={onHide}>
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sdm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">
              {headStudent ? "Mock interview evaluation" : "Batch"}
            </span>
            <h3 className="pg-modal-title" id="sdm-title">
              {modalTitle()}
            </h3>
            <p className="pg-modal-sub">{modalSub()}</p>
          </div>
          <button
            type="button"
            className="pg-modal-close"
            aria-label="Close"
            onClick={onHide}
          >
            <FaTimes />
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? <PopupDone message={done} onClose={onHide} /> : renderBody()}
        </div>

        {!done && (
        <footer className="pg-modal-foot">
          {isListMode && showDetail ? (
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                setShowDetail(false);
                setDetailStudent(null);
                setIsEditingEvaluation(false);
              }}
            >
              ← Back to students
            </button>
          ) : (
            <span />
          )}

          <div className="pg-modal-foot-acts">
            <button type="button" className="pg-btn" onClick={onHide}>
              Close
            </button>
            {(!isListMode || (isListMode && showDetail)) && (!hasExistingMockResult || isEditingEvaluation) && (
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => handleMockInterviewSubmit(isListMode ? detailStudent : studentData?.student)}
                disabled={submitting || loadingMockResult}
              >
                {submitting
                  ? (isEditingEvaluation ? "Updating..." : "Submitting...")
                  : (isEditingEvaluation ? "Update evaluation" : "Submit evaluation")}
              </button>
            )}
          </div>
        </footer>
        )}
      </div>
    </div>
  );
};

export default StudentDetailModal;